import { Task, TaskType } from '../../types';
import { RandomizedAdProvider } from './boost/RandomizedAdProvider';
import {
  updateBannerCpmProgress,
  claimBannerCpmReward,
  getBannerCpmTasks,
} from '../../../../api/modules/Tasks/banner_cpm';
import { SponsorsService } from '../../services/SponsorsService';
import { useNotificationStore } from '../../../../store/NotificationStore';
import axios from 'axios';
import { settings } from '@/api/config';
import { getInitData } from '@/utils/getInitData';

interface BannerCpmTask {
  id: number;
  type: string;
  view_details: {
    title: string;
  };
  details: {
    last_view: string;
    sleep_time_end: string;
  };
  rewards: {
    stars: number;
  };
  status: string;
  target_progress: number;
  user_progress: number;
}

interface BannerCpmResponse {
  status: string;
  tasks: BannerCpmTask[];
}

// CPM view API call
async function cpmViewApi(platform: string) {
  if (!getInitData()) throw new Error('initData отсутствует');
  const params = { initData: getInitData(), platform };
  const url = `${settings.url}api/banners/banners-cpm/view`;
  return axios.post(url, null, { params });
}

export class BannerCpmAdapter {
  private static randomizedProvider = new RandomizedAdProvider();
  private static processingTasks = new Set<number>();
  private static readonly COOLDOWN_SECONDS = 0;

  static adaptTasks(response: BannerCpmResponse): Task[] {
    if (!response.tasks || !Array.isArray(response.tasks)) {
      return [];
    }

    // Фильтруем уникальные задания по ID и статусу
    const uniqueTasks = response.tasks.reduce((acc, task) => {
      // Если задание уже есть в аккумуляторе, пропускаем его
      if (acc.some((t) => t.id === task.id)) {
        return acc;
      }

      const timeRemaining = this.getTimeRemaining(task.details.last_view);
      const isButtonEnabled = timeRemaining <= 0;
      console.log('taskUnique', task);
      // Если задание имеет статус claim, добавляем его как DONE
      if (task.status === 'claim') {
        acc.push({
          id: task.id,
          provider: 'BANNER_CPM',
          title: task.view_details.title,
          description: '',
          rewards: {
            stars: task.rewards.stars,
          },
          status: 'DONE',
          progress: {
            current: task.user_progress,
            total: task.target_progress,
          },
          link: '',
          type: 'sponsors' as TaskType,
          buttonText: 'Собрать',
          visibility: true,
          isButtonEnabled: true,
          date_end: task.details.sleep_time_end,
        });
        return acc;
      }

      // Для остальных заданий
      acc.push({
        id: task.id,
        provider: 'BANNER_CPM',
        title: task.view_details.title,
        description: '',
        rewards: {
          stars: task.rewards.stars,
        },
        status: this.mapStatus(
          task.status,
          task.user_progress,
          task.target_progress,
        ),
        progress: {
          current: task.user_progress,
          total: task.target_progress,
        },
        link: '',
        type: 'sponsors' as TaskType,
        buttonText: this.getButtonText(
          task.user_progress,
          task.target_progress,
        ),
        visibility: true,
        isButtonEnabled,
        timeRemaining,
        date_end: task.details.sleep_time_end,
      });
      return acc;
    }, [] as Task[]);

    return uniqueTasks;
  }

  private static getTimeRemaining(lastView: string): number {
    const lastViewDate = new Date(lastView);
    const currentDate = new Date();
    const diffInSeconds = Math.floor(
      (currentDate.getTime() - lastViewDate.getTime()) / 1000,
    );
    return Math.max(0, this.COOLDOWN_SECONDS - diffInSeconds);
  }

  private static mapStatus(
    status: string,
    userProgress: number,
    targetProgress: number,
  ): Task['status'] {
    if (userProgress >= targetProgress) {
      return 'DONE';
    }

    switch (status) {
      case 'active':
        return 'NOT_STARTED';
      case 'completed':
        return 'DONE';
      case 'claimed':
        return 'DONE';
      case 'sleep':
        return 'SLEEP';
      default:
        return 'NOT_STARTED';
    }
  }

  private static getButtonText(
    userProgress: number,
    targetProgress: number,
  ): string {
    if (userProgress >= targetProgress) {
      return 'Собрать';
    }
    return 'Посмотреть';
  }

  static async handleTaskClick(
    taskId: number,
    currentProgress: number,
    targetProgress: number,
    platform?: 'adextra' | 'onclick' | 'moneteg',
  ): Promise<void> {
    try {
      // Проверяем, не обрабатывается ли уже это задание
      if (this.processingTasks.has(taskId)) {
        console.log('Задание уже обрабатывается');
        return;
      }

      // Получаем текущее задание
      const currentTasks = await getBannerCpmTasks();
      const currentTask = currentTasks.tasks.find(
        (t: BannerCpmTask) => t.id === taskId,
      );

      if (!currentTask) {
        console.error('❌ Задание не найдено');
        return;
      }

      // Проверяем время ожидания
      const timeRemaining = this.getTimeRemaining(
        currentTask.details.last_view,
      );
      if (timeRemaining > 0) {
        console.log(`⏳ Необходимо подождать еще ${timeRemaining} секунд`);
        return;
      }

      // Если прогресс достиг цели или статус DONE, вызываем claim
      if (currentProgress >= targetProgress) {
        this.processingTasks.add(taskId);

        try {
          console.log('🎯 Начинаем получение награды для задания:', taskId);
          const claimResponse = await claimBannerCpmReward(taskId);
          console.log('🎁 Banner CPM task claimed:', claimResponse);

          // Показываем уведомление об успешном получении награды
          const { setNotification } = useNotificationStore.getState();
          setNotification({
            type: 'Success',
            message: 'Награда успешно получена!',
          });

          // Убираем дублирующий запрос - новые задачи будут получены в TaskItem
          // console.log('🔄 Получаем новые задания после получения награды');
          // const newTasks = await getBannerCpmTasks();
          // const adaptedTasks = this.adaptTasks(newTasks);

          // console.log('📝 Обновляем список заданий');
          // SponsorsService.updateTasks(adaptedTasks);
        } catch (error) {
          console.error('❌ Ошибка при получении награды:', error);
          throw error;
        } finally {
          this.processingTasks.delete(taskId);
        }
        return;
      }

      // --- CPM через adextra или onclick ---
      if (platform === 'adextra' || platform === 'onclick') {
        this.processingTasks.add(taskId);
        try {
          // Импортируем нужный провайдер
          let result: 'success' | 'user_cancelled' | 'ads_not_found' =
            'ads_not_found';
          if (platform === 'adextra') {
            const { AdextraBoostProvider } = await import(
              './boost/AdextraBoostProvider'
            );
            const adextra = new AdextraBoostProvider();
            result = await adextra.showAd();
          } else if (platform === 'onclick') {
            const { OnClickABoostProvider } = await import(
              './boost/OnClickABoostProvider'
            );
            const onclick = new OnClickABoostProvider();
            const tasks = await onclick.getAvailableTasks();
            if (tasks.length > 0) {
              result = await tasks[0].onClick();
            }
          }
          if (result !== 'success') {
            const { setNotification } = useNotificationStore.getState();
            setNotification({
              type: 'Error',
              message: 'Реклама не была показана.',
            });
            return;
          }
          // После успешного просмотра отправляем view API
          await cpmViewApi(platform);
          const { setNotification } = useNotificationStore.getState();
          setNotification({
            type: 'Success',
            message: 'Реклама просмотрена, прогресс увеличен.',
          });
        } finally {
          this.processingTasks.delete(taskId);
        }
        return;
      }

      // Получаем доступные задания
      const availableTasks = await this.randomizedProvider.getAvailableTasks(0);
      console.log('available Tasks', availableTasks);
      if (availableTasks.length > 0) {
        this.processingTasks.add(taskId);

        try {
          // Берем первое задание и запускаем его
          const task = availableTasks[0];
          const result = await task.onClick();

          // Проверяем результат выполнения
          if (result !== 'success') {
            console.log('❌ Реклама не была показана:', result);
            return;
          }

          // Ждем завершения просмотра рекламы
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Обновляем прогресс задания
          console.log('🔄 Обновляем прогресс задания');
          const updatedTask = await updateBannerCpmProgress();
          console.log('🎯 Banner CPM task progress updated:', updatedTask);

          // Создаем обновленное задание с новым временем ожидания
          const timeRemaining = this.COOLDOWN_SECONDS;
          const adaptedTask: Task = {
            id: updatedTask.id,
            provider: 'BANNER_CPM',
            title: updatedTask.view_details.title,
            description: '',
            rewards: {
              stars: updatedTask.rewards.stars,
            },
            status: this.mapStatus(
              updatedTask.status,
              updatedTask.user_progress,
              updatedTask.target_progress,
            ),
            progress: {
              current: updatedTask.user_progress,
              total: updatedTask.target_progress,
            },
            link: '',
            type: 'sponsors' as TaskType,
            buttonText: this.getButtonText(
              updatedTask.user_progress,
              updatedTask.target_progress,
            ),
            visibility: true,
            isButtonEnabled: false,
            timeRemaining,
          };

          // Обновляем задание в списке
          console.log('📝 Обновляем задание в списке');
          SponsorsService.updateTask(adaptedTask);

          // Если прогресс достиг цели, получаем новые задания
          if (updatedTask.user_progress >= updatedTask.target_progress) {
            console.log('🎯 Прогресс достиг цели, получаем новые задания');
            const newTasks = await getBannerCpmTasks();
            const adaptedTasks = this.adaptTasks(newTasks);

            // Обновляем все задания
            console.log('📝 Обновляем все задания');
            SponsorsService.updateTasks(adaptedTasks);
          }

          // Показываем уведомление о просмотре рекламы и увеличении прогресса
          const { setNotification } = useNotificationStore.getState();
          setNotification({
            type: 'Success',
            message: 'Реклама просмотрена, прогресс увеличен.',
          });
        } finally {
          this.processingTasks.delete(taskId);
        }
      } else {
        console.error('❌ No available tasks found');
      }
    } catch (error) {
      console.error('❌ BannerCpmAdapter.handleTaskClick - error:', error);
      this.processingTasks.delete(taskId);
      throw error;
    }
  }
}
